// frontend/src/components/MutantBox.js
import React, { useEffect, useState, useCallback, useContext } from 'react';
import {
  fetchMutantBox,
  postMutantBoxAction,
  generateMutantBox,
  fetchMutantBoxContext,
  fetchJobStatus
} from '../api';
import { AuthContext } from '../context/AuthContext';
import MentalLoadScreen from './MentalLoadScreen';
import EnrichedText from './EnrichedText';
import ReadableText from './ReadableText';
import ImageLightbox from './ImageLightbox';
import QuotaAlert from './QuotaAlert';
import HyperstitionYoutubePlayer from './HyperstitionYoutubePlayer';
import './MutantBox.css';
import { normalizeLanguage, useI18n } from '../i18n';
import { useProgression } from '../context/ProgressionContext';

function safeParseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch (e) { return null; }
}

// Función para limpiar texto sucio que contenga JSON
function cleanJsonFromText(text, fallbackText = "El portal está en silencio.", messageText = "Mensaje recibido.") {
  if (!text) return fallbackText;
  if (typeof text !== 'string') return String(text);
  
  const trimmed = text.trim();
  // Si parece un objeto JSON, intentamos extraer el mensaje real
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || trimmed.includes('"lead":')) {
    try {
      // A veces el LLM devuelve texto + JSON. Intentamos extraer el bloque JSON primero.
      const jsonMatch = trimmed.match(/(\{[\s\S]*\})/);
      const jsonStr = jsonMatch ? jsonMatch[1] : trimmed;
      
      const obj = JSON.parse(jsonStr);
      return obj.lead || obj.text || obj.message || obj.content || messageText;
    } catch (e) {
      // Si falla el parseo, intentamos una limpieza manual por regex
      const match = trimmed.match(/"lead"\s*:\s*"([^"]+)"/);
      if (match && match[1]) return match[1];
      return trimmed; // Fallback al original
    }
  }
  return trimmed;
}

function buildPortalTitle(rawSuffix, language, t) {
  const lang = normalizeLanguage(language);
  let suffix = String(rawSuffix || t('mutantBox.defaultSuffix') || '').trim();

  if (lang === 'en') {
    suffix = suffix
      .replace(/^portal\s+/i, '')
      .replace(/^de\s+/i, 'of ');
    if (/conciencia/i.test(suffix)) suffix = 'of Consciousness';
    if (!suffix) suffix = t('mutantBox.defaultSuffix');
    return /^of\s+/i.test(suffix)
      ? t('mutantBox.portalTitle', { suffix })
      : t('mutantBox.portalTitleAdjective', { suffix });
  }

  suffix = suffix.replace(/^portal\s+/i, '');
  if (/^(of\s+)?consciousness$/i.test(suffix)) suffix = 'de Conciencia';
  if (!suffix) suffix = t('mutantBox.defaultSuffix');
  return t('mutantBox.portalTitle', { suffix });
}

export default function MutantBox({ className = '', userId: propUserId = null }) {
  const { user, refreshUser } = useContext(AuthContext);
  const { refreshState } = useProgression() || {};
  const { language, t } = useI18n();
  const userLang = language;
  const userId = propUserId || user?.id;

  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [textInputValue, setTextInputValue] = useState('');
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const [boxData, setBoxData] = useState(null);
  const [currentContent, setCurrentContent] = useState(() => {
    try {
      const cached = localStorage.getItem('q_last_mutant_content');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [error, setError] = useState('');
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);
  const [backgroundJob, setBackgroundJob] = useState(null);

  // State for Context Modal
  const [showContextModal, setShowContextModal] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextData, setContextData] = useState(null);

  // State for Lightbox (Enlarge image)
  const [showLightbox, setShowLightbox] = useState(false);

  // State for explanation toggle
  const [showExplanation, setShowExplanation] = useState(false);

  const loadBox = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetchMutantBox({ userId });
      const box = res?.data?.box || res?.data || null;
      setBoxData(box);
      if (box && Array.isArray(box.contents) && box.contents.length > 0) {
        const content = box.contents[0];
        setCurrentContent(content);
        localStorage.setItem('q_last_mutant_content', JSON.stringify(content));
        setShowExplanation(false); // Reset explanation on new content
      }
    } catch (e) {
      console.error('Error loading MutantBox:', e);
      setError(t('mutantBox.noSignal'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    loadBox();
  }, [loadBox]);

  const applyGeneratedContent = useCallback((data) => {
    if (!data || !data.content) return false;
    if ((data.content.image_data || data.content.imageData) && refreshUser) {
      refreshUser();
    }

    const content = data.content;
    setCurrentContent(content);
    localStorage.setItem('q_last_mutant_content', JSON.stringify(content));

    const newBox = data.box || data;
    setBoxData(prev => ({
      ...(prev || {}),
      ...(newBox || {}),
      contents: [content, ...(prev?.contents || [])]
    }));
    setShowExplanation(false);
    return true;
  }, [refreshUser]);

  useEffect(() => {
    if (!backgroundJob?.id || backgroundJob.status !== 'pending') return undefined;

    const intervalId = window.setInterval(async () => {
      try {
        const res = await fetchJobStatus(backgroundJob.id);
        const job = res.data;
        if (job.status === 'completed') {
          applyGeneratedContent(job.result);
          if (refreshState) refreshState();
          setBackgroundJob(null);
          window.clearInterval(intervalId);
        } else if (job.status === 'error') {
          setError(t('mutantBox.delayedError'));
          setBackgroundJob(null);
          window.clearInterval(intervalId);
        }
      } catch (e) {
        console.error('Error polling MutantBox job:', e);
      }
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [backgroundJob, applyGeneratedContent, t]);

  const handleManualGenerate = async (extraParams = {}) => {
    setMutating(true);
    window.dispatchEvent(new CustomEvent('q-stamina-pulse'));
    try {
      const res = await generateMutantBox({
        userId,
        translateTo: userLang,
        ...extraParams
      });

      if (res.status === 202 && res.data?.jobId) {
        setBackgroundJob({ id: res.data.jobId, status: 'pending' });
      } else if (!applyGeneratedContent(res.data)) {
        await loadBox();
      }
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.data?.quotaExceeded) {
        setShowQuotaAlert(true);
      }
      setError(t('mutantBox.forceError'));
    } finally {
      setMutating(false);
    }
  };

  const handleFetchContext = async () => {
    if (!boxData?.id) return;
    setContextLoading(true);
    setShowContextModal(true);
    setError('');
    try {
      const res = await fetchMutantBoxContext(boxData.id);
      setContextData(res.data?.data || res.data);
    } catch (e) {
      console.error('Error fetching context:', e);
      setError(t('mutantBox.contextError'));
    } finally {
      setContextLoading(false);
    }
  };


  const handleTextInputSubmit = () => {
    if (!textInputValue.trim()) return;
    const action = {
      label: "User Feedback",
      value: "text_feedback",
      type: "text_input",
      metadata: { text: textInputValue }
    };
    handleAction(action);
    setTextInputValue('');
  };

  const handleAction = async (action) => {
    // Fix: If no box ID (virgin state), treat action as a generator trigger
    if (!boxData?.id) {
       await handleManualGenerate({ triggerBy: action.value });
       return;
    }
    
    setMutating(true);
    window.dispatchEvent(new CustomEvent('q-stamina-pulse'));
    setError('');

    try {
      const res = await postMutantBoxAction(boxData.id, {
        contentId: currentContent?.id,
        actionType: action.type || 'interact',
        metadata: { label: action.label, value: action.value, ...(action.metadata || {}) }
      });

      if (res.data && res.data.newContent) {
        // If new content contains an image, refresh user quota state
        if ((res.data.newContent.image_data || res.data.newContent.imageData) && refreshUser) {
            refreshUser();
        }

        const content = res.data.newContent;
        setCurrentContent(content);
        localStorage.setItem('q_last_mutant_content', JSON.stringify(content));

        // Fix Critical Bug: The state was getting "stuck" in "Pintando realidad..."
        // because we weren't waiting for the countdown update.
        // We now prioritize the box from response if available, or force a sync wait.

        let newBoxUpdates = {};

        if (res.data.box) {
             newBoxUpdates = res.data.box;
        } else {
             // Fallback: sync from server to get updated countdown before unlocking UI
             try {
                 const r = await fetchMutantBox({ userId });
                 newBoxUpdates = r?.data?.box || r?.data || {};
             } catch(err) {
                 console.error("Box sync failed", err);
             }
        }

        setBoxData(prev => ({
            ...(prev || {}),
            ...newBoxUpdates,
            contents: [res.data.newContent, ...(prev?.contents || [])]
        }));

        setShowExplanation(false);
      } else {
        await loadBox();
      }

      if (refreshState) refreshState();

    } catch (e) {
      console.error('Error en acción:', e);
      if (e?.response?.status === 403 || e?.response?.data?.quotaExceeded) {
        setShowQuotaAlert(true);
      }
      setError(t('mutantBox.actionError'));
    } finally {
      setMutating(false);
    }
  };

  const meta = currentContent ? (safeParseMaybeJson(currentContent.meta) || {}) : {};
  
  // 1. Obtenemos el texto crudo de donde sea que venga
  let rawText = meta.lead || currentContent?.content_text || currentContent?.content;
  
  // 2. Lo limpiamos agresivamente para quitar JSON visual
  const leadText = cleanJsonFromText(rawText, t('mutantBox.defaultSilent'), t('mutantBox.messageReceived'));
  
  // Images
  const imageData = currentContent?.image_data || currentContent?.imageData || null;
  const imageExplanation = currentContent?.image_explanation || currentContent?.imageExplanation || null;

  const metaLanguage = meta.language ? normalizeLanguage(meta.language) : null;
  const canUseLocalizedMeta = metaLanguage === language;

  // Title Logic
  const portalSuffix = canUseLocalizedMeta ? meta.portalSuffix : null;
  const fullTitle = buildPortalTitle(portalSuffix, language, t);

  let actionsToRender = [];
  if (canUseLocalizedMeta && Array.isArray(meta.actions) && meta.actions.length > 0) {
    actionsToRender = meta.actions;
  } else {
    actionsToRender = [
      { label: t('mutantBox.surprise'), emoji: "🎲", value: "surprise" },
      { label: t('mutantBox.analyze'), emoji: "👁️", value: "analyze" }
    ];
  }

  const newsList = Array.isArray(meta.newsSample) ? meta.newsSample : [];

  // Determine if we are in "Painting" mode (generating an image)
  // Logic: if imageCountdown <= 0 OR undefined (first use), the next generation includes an image.
  // Note: we check both camelCase and snake_case for safety.
  const countdown = boxData?.imageCountdown ?? boxData?.image_countdown;
  const isPainting = boxData && (
    countdown === undefined ||
    countdown === null ||
    countdown <= 0
  );

  // Debug log to trace state (requested by plan)
  useEffect(() => {
      console.log('[MutantBox] Render State:', {
          hasBoxData: !!boxData,
          imageCountdown: boxData?.imageCountdown,
          isPainting,
          mutating
      });
  }, [boxData, isPainting, mutating]);

  if (!userId) return null;

  return (
    <div id="mutant-box-main" className={`mutantbox-root enhanced ${className}`} data-agent-module="mutant-box">
      
      {mutating && (
        <MentalLoadScreen
          loading={mutating}
          message={backgroundJob ? t('mutantBox.delayedGeneration') : (isPainting ? t('mutantBox.painting') : t('mutantBox.mutating'))}
          showSpinner={true}
          spinnerVariant={isPainting ? 'multicolor' : 'green'}
          showDice={false}
        />
      )}

      {backgroundJob && (
        <div className="mutantbox-delayed-job" role="status">
          <span>{t('mutantBox.delayedGeneration')}</span>
          <button type="button" onClick={() => setBackgroundJob(null)}>
            {t('mutantBox.keepExploring')}
          </button>
        </div>
      )}

      <div className="mutantbox-header">
        <div className="mutantbox-title-row">
          <span className="mutantbox-icon">🌀</span>
          <h3 className="mutantbox-dynamic-title" key={fullTitle}>
            {fullTitle}
          </h3>
        </div>
        <div className="mutantbox-controls">
          <div className="mutantbox-action-row">
            <button
              id="btn-mutant-context"
              data-agent-action="open-context"
              onClick={handleFetchContext}
              className="mutantbox-context-btn"
              title={t('mutantBox.contextTitle')}
              disabled={mutating || !currentContent}
            >
              <span className="btn-icon-globe">🌐</span>
            </button>
            <button
              id="btn-generate-mutation"
              data-agent-action="generate-mutation"
              onClick={() => handleManualGenerate({ resetTopic: true })}
              className="mutantbox-refresh-btn"
              title={t('mutantBox.newSignalTitle')}
              disabled={mutating}
            >
              <span className="btn-icon-bolt">⚡</span> {t('mutantBox.newSignal')}
            </button>
          </div>
        </div>
      </div>

      <div className="mutantbox-body">
        {loading && !currentContent ? (
          <div className="mutantbox-loading">{t('mutantBox.connecting')}</div>
        ) : (
          <>
            {meta.videoId ? (
              <div className={`mutant-box-video-container ${isVideoExpanded ? 'expanded' : ''}`}>
                <div className="mutant-box-video-header">
                   <button
                     className="mutant-box-video-expand-btn"
                     onClick={() => setIsVideoExpanded(!isVideoExpanded)}
                     title={isVideoExpanded ? 'Reducir' : 'Expandir'}
                   >
                     {isVideoExpanded ? '↙ Reducir' : '↗ Expandir'}
                   </button>
                </div>
                <HyperstitionYoutubePlayer videoId={meta.videoId} title="Mutant Box Video" muted={true} controls={true} />
                {meta.requiresTextInput && (
                  <div className="mutantbox-input-container">
                    <input
                      type="text"
                      className="mutantbox-text-input"
                      placeholder="Escribe tu respuesta..."
                      value={textInputValue}
                      onChange={(e) => setTextInputValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleTextInputSubmit(); }}
                    />
                    <button onClick={handleTextInputSubmit} className="mutantbox-cyber-btn">Enviar</button>
                  </div>
                )}
                {actionsToRender.length > 0 && (
                  <div className="mutantbox-actions-grid">
                    {actionsToRender.map((act, idx) => (
                      <button
                        key={idx}
                        className="mutantbox-creative-btn mutant-action-btn"
                        data-agent-mutant-action
                        data-agent-action-label={act.label || ""}
                        data-agent-content-id={String(currentContent?.id || "")}
                        onClick={() => handleAction(act)}
                        disabled={mutating}
                      >
                        <span className="btn-emoji">{act.metadata?.emoji || act.emoji || '⚪'}</span>
                        <span className="btn-label">{act.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div id="mutant-box-current" data-agent-mutant-current="true" className="mutantbox-message-card">
                  <div id="mutant-box-lead" data-agent-readable="mutant-lead" className="mutantbox-message-content">
                    <span aria-hidden="true">"</span>
                    <ReadableText text={leadText} label={t('mutantBox.readSignal')}>
                      <EnrichedText
                        text={leadText}
                        askingPoints={meta.askingPoints || meta.sources || []}
                      />
                    </ReadableText>
                    <span aria-hidden="true">"</span>
                  </div>
                </div>

                <div className="mutantbox-actions-grid">
                  {actionsToRender.map((act, idx) => (
                    <button
                      key={idx}
                      className="mutantbox-creative-btn mutant-action-btn"
                      data-agent-mutant-action
                      data-agent-action-label={act.label || ""}
                      data-agent-content-id={String(currentContent?.id || "")}
                      onClick={() => handleAction(act)}
                      disabled={mutating}
                    >
                      <span className="btn-emoji">{act.metadata?.emoji || act.emoji || '⚪'}</span>
                      <span className="btn-label">{act.label}</span>
                    </button>
                  ))}
                </div>

                {/* Banner Apaisado Mutante */}
                {imageData && (
                  <>
                    <div className="mutant-box-standard-container">
                      <img
                        id="mutant-box-image"
                        data-agent-mutant-image="true"
                        data-agent-action="expand-image"
                        src={imageData}
                        className="mutant-img-collapsed"
                        onClick={() => setShowLightbox(true)}
                        alt={t('mutantBox.imageAlt')}
                      />
                    </div>
                    {imageExplanation && (
                      <div className="mutantbox-explanation-wrapper">
                        <button
                          id="btn-mutant-explanation"
                          data-agent-action="toggle-explanation"
                          className="mutantbox-explanation-toggle"
                          onClick={() => setShowExplanation(!showExplanation)}
                        >
                          <span className={`toggle-arrow ${showExplanation ? 'open' : ''}`}>▼</span>
                          <span className="toggle-label">{t('mutantBox.whyImage')}</span>
                        </button>
                        {showExplanation && (
                          <div className="mutantbox-explanation-content">
                            {imageExplanation}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {newsList.length > 0 && (
              <div id="mutant-box-history" data-agent-history="mutant-box" className="mutantbox-news-ticker">
                <span className="ticker-label">{t('mutantBox.worldEchoes')}</span>
                <div className="ticker-items">
                  {newsList.slice(0, 2).map((n, i) => (
                    <a key={i} href={n.link} target="_blank" rel="noopener noreferrer" className="ticker-link">
                      {n.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {error && <div className="mutantbox-error-bar">{error}</div>}
          </>
        )}
      </div>

      {showLightbox && imageData && (
        <ImageLightbox
          src={imageData}
          alt={t('mutantBox.imageAlt')}
          onClose={() => setShowLightbox(false)}
        />
      )}

      <QuotaAlert
        isOpen={showQuotaAlert}
        onClose={() => setShowQuotaAlert(false)}
      />

      {showContextModal && (
        <div id="mutant-box-context-modal" data-agent-modal="mutant-context" className="mutant-modal-overlay context-modal">
          <div className="mutant-modal-content">
            <div className="context-modal-header">
              <h4 className="context-modal-title">{t('mutantBox.contextQ')}</h4>
              <button
                type="button"
                className="mutant-modal-close context-modal-close"
                onClick={() => setShowContextModal(false)}
                aria-label={t('common.close')}
                title={t('common.close')}
              >
                ✖
              </button>
            </div>

            {contextLoading ? (
              <div className="context-modal-loading">
                <div className="mutantbox-spinner" />
                <p>{t('mutantBox.analyzing')}</p>
              </div>
            ) : contextData ? (
              <div className="context-modal-body">
                <div className="mutant-context-readable">
                  <ReadableText
                    text={contextData.explanation}
                    label={`${t('audio.readWithAgent')}: ${t('mutantBox.contextQ')}`}
                    className="context-readable-text"
                    alwaysShowPoint
                  >
                    <span id="mutant-box-context-text" data-agent-readable="mutant-context" className="context-explanation">
                      {contextData.explanation}
                    </span>
                  </ReadableText>
                </div>

                {contextData.videoId ? (
                  <div className="context-video-container">
                    <iframe
                      title={t('mutantBox.contextVideo')}
                      src={`https://www.youtube.com/embed/${contextData.videoId}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="context-video-fallback">
                    <p className="fallback-text">{t('mutantBox.videoUnavailable')}</p>
                    <a
                      href={`https://www.youtube.com/results?search_query=${encodeURIComponent(contextData.search_query || 'conciencia')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fallback-link-btn"
                    >
                      <span className="btn-icon">🔍</span> {t('mutantBox.exploreNetwork')}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <p className="context-error">{t('mutantBox.contextLoadError')}</p>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
